import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { RunSheet } from "@/components/run/RunSheet";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ruleSummary, type ProgressionPreview, type RoundRow, type WorkspaceProps } from "./model";
import { useRoundWorkspace } from "./useRoundWorkspace";
import { useRoundDraft } from "./useRoundDraft";
import "./Rounds.css";

export function ProgressionSheet(props: WorkspaceProps & { roundId: string; onClose: () => void }) {
  const transport = props.transport ?? api,
    base = `/api/competitions/${props.competitionId}`,
    path = `${base}/rounds/${props.roundId}`;
  const read = useCallback(async () => {
    const [round, preview] = await Promise.all([
      transport.get<RoundRow>(path),
      transport.post<ProgressionPreview>(`${path}/compute-progression`, undefined, {
        canRetry: () => false,
      }),
    ]);
    const targets = await transport.get<RoundRow[]>(
      `${base}/divisions/${round.division_id}/rounds`,
    );
    return { round, preview, targets };
  }, [base, path, transport]);
  const state = useRoundWorkspace(props, props.roundId, read),
    data = state.data;
  const [confirm, setConfirm] = useState<{
    kind: "apply" | "undo";
    version: number;
    token?: string;
    count: number;
    guard: () => boolean;
  } | null>(null);
  const [lifecycle, setLifecycle] = useState<{
    kind: "abandon" | "resume" | "reorder-run2";
    version: number;
    guard: () => boolean;
    mode?: "continue" | "rerun";
  } | null>(null);
  const [reason, setReason] = useRoundDraft(props, props.roundId, "");
  const [manual, setManual] = useRoundDraft(props, props.roundId, false),
    [rider, setRider] = useRoundDraft(props, props.roundId, ""),
    [target, setTarget] = useRoundDraft(props, props.roundId, "");
  const admin = props.roles.includes("admin"),
    canManual = props.manualEnabled && (admin || props.roles.includes("producer"));
  const canApply = admin && props.rulesAllowed && props.rulesEnabled && props.manualEnabled;
  const count = data?.preview.rules.reduce((total, r) => total + r.advancing.length, 0) ?? 0;
  const disabled = !state.ready || state.busy;
  return (
    <div className="sc-rounds sc-round-sheet">
      <RunSheet title="Advance round" close={props.onClose}>
        <div className="sc-rounds" aria-busy={state.loading}>
          <p className="sc-round-muted">
            {data?.round.name ?? "Round"}
            {data && ` · ${data.round.status}`}
          </p>
          {data?.round.progression_may_change && (
            <p role="status" className="sc-round-warning">
              Progression may change after a corrected posting. Review the updated preview before
              starting the affected heats; undo and reapply where permitted.
            </p>
          )}
          {!state.online && (
            <p role="status" className="sc-round-warning">
              Offline — showing saved data. Reconnect to advance.
            </p>
          )}
          {state.loading && <p role="status">Refreshing the preview…</p>}
          {state.error && (
            <div role="alert">
              <p>{state.error}</p>
              <button onClick={() => void state.reload().catch(() => {})}>Retry</button>
            </div>
          )}
          {data?.preview.rules.map((rule) => (
            <section key={rule.rule_id} className="sc-round-card">
              <h3>
                {rule.to_round_name ??
                  data.targets.find((r) => r.id === rule.to_round_id)?.name ??
                  "Next round"}
              </h3>
              {rule.from_places && (
                <p>
                  {ruleSummary(
                    rule.from_places,
                    rule.by ?? "heat",
                    rule.to_round_name ?? "Next round",
                  )}
                </p>
              )}
              <div
                className="sc-round-table"
                tabIndex={0}
                role="region"
                aria-label="Advancing riders"
              >
                <table>
                  <thead>
                    <tr>
                      <th>Place</th>
                      <th>Rider</th>
                      <th>Heat</th>
                      <th>Seed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rule.advancing.map((row) => {
                      const seed = rule.seeding.find((s) => s.heat_rider_id === row.heat_rider_id);
                      return (
                        <tr key={row.heat_rider_id}>
                          <td>{row.reason === "bye" ? "Bye" : row.rank}</td>
                          <th scope="row">
                            {row.name ??
                              data.round.riders?.find((r) => r.id === row.heat_rider_id)?.name ??
                              "Rider"}
                          </th>
                          <td>{(seed?.target_heat_index ?? 0) + 1}</td>
                          <td>{seed?.seed ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          {data?.preview.target_orders?.some((layout) =>
            layout.entries?.some((entry) => entry.existing),
          ) && (
            <section className="sc-round-card">
              <h3>Full destination draw</h3>
              <p>
                Existing riders remain in the draw. Review their positions alongside the advancing
                riders.
              </p>
              {data.preview.target_orders.map((layout) => (
                <section key={`${layout.round_id}:${layout.heat_index}`}>
                  <h4>
                    {data.targets.find((round) => round.id === layout.round_id)?.name ??
                      "Next round"}{" "}
                    · Heat {layout.heat_index + 1}
                  </h4>
                  <ol>
                    {layout.entries?.map((entry) => (
                      <li key={entry.source_key}>
                        {entry.name} · seed {entry.seed ?? "—"} ·{" "}
                        {entry.existing ? "already in this heat" : "advancing"}
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </section>
          )}
          {data && !data.preview.rules.length && (
            <p>
              No progression rules are saved. Use Manual promote, or configure rules in Setup →
              Format.
            </p>
          )}
          {data && data.preview.blocked.length > 0 && (
            <section className="sc-round-warning" role="status">
              <h3>Resolve before advancing</h3>
              <ul>
                {data.preview.blocked.map((block, index) => (
                  <li key={index}>
                    {block.reason.replaceAll("_", " ")}
                    {block.reason === "needs_manual" && (
                      <>
                        {" · "}
                        <Link
                          to={`/competition/${props.competitionId}/ties/round/${props.roundId}`}
                        >
                          Resolve tie
                        </Link>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <p className="sc-round-summary">
            {count} {count === 1 ? "rider advances" : "riders advance"}. Scores are unchanged.
          </p>
          <button
            className="btn btn-primary"
            disabled={
              disabled ||
              !canApply ||
              !count ||
              !!data?.preview.blocked.length ||
              !!data?.round.progression?.applied
            }
            onClick={() => {
              if (data)
                setConfirm({
                  kind: "apply",
                  version: data.preview.version,
                  token: data.preview.preview_token,
                  count,
                  guard: state.capture(),
                });
            }}
          >
            Review and apply
          </button>
          {(!props.rulesEnabled || !props.manualEnabled) && (
            <p className="sc-round-muted">
              Round execution is disabled. Enable Rounds and Rules in Setup → Format to apply
              progression; manual promotion needs Rounds enabled.
            </p>
          )}
          {!admin && (
            <p className="sc-round-muted">
              Admin permission is required to apply or undo progression.
            </p>
          )}
          {admin && !props.rulesAllowed && (
            <p className="sc-round-muted">
              Rules require Club Unlimited or an Event pass. Manual promote is available.
            </p>
          )}
          <button
            disabled={!state.online || state.loading || state.busy}
            onClick={() => void state.reload().catch(() => {})}
          >
            Refresh preview
          </button>
          <details>
            <summary>More</summary>
            <div className="sc-round-actions">
              <button disabled={disabled || !canManual} onClick={() => setManual(!manual)}>
                Manual promote
              </button>
              <button
                disabled={disabled || !canApply || !data?.round.progression?.can_undo}
                onClick={() => {
                  if (data)
                    setConfirm({
                      kind: "undo",
                      version: data.round.version,
                      count: data.round.progression?.promoted_count ?? 0,
                      guard: state.capture(),
                    });
                }}
              >
                Undo progression
              </button>
              {!!data?.round.progression?.scored_heat_ids.length && (
                <p>Undo is unavailable because a destination heat has scores.</p>
              )}
            </div>
          </details>
          {data && (
            <details>
              <summary>Round lifecycle</summary>
              <div className="sc-round-actions">
                {data.round.status !== "abandoned" && (
                  <button
                    disabled={
                      disabled ||
                      !props.manualEnabled ||
                      !(admin || props.roles.includes("chief_judge")) ||
                      !!data.round.progression?.applied
                    }
                    onClick={() => {
                      setReason("");
                      setLifecycle({
                        kind: "abandon",
                        version: data.round.version,
                        guard: state.capture(),
                      });
                    }}
                  >
                    Abandon round
                  </button>
                )}
                {data.round.status === "abandoned" && (
                  <>
                    <button
                      disabled={
                        disabled ||
                        !props.manualEnabled ||
                        !(admin || props.roles.includes("chief_judge"))
                      }
                      onClick={() =>
                        setLifecycle({
                          kind: "resume",
                          mode: "continue",
                          version: data.round.version,
                          guard: state.capture(),
                        })
                      }
                    >
                      Continue uncompleted heats
                    </button>
                    <button
                      disabled={
                        disabled ||
                        !props.manualEnabled ||
                        !(admin || props.roles.includes("chief_judge"))
                      }
                      onClick={() =>
                        setLifecycle({
                          kind: "resume",
                          mode: "rerun",
                          version: data.round.version,
                          guard: state.capture(),
                        })
                      }
                    >
                      Rerun the whole round
                    </button>
                  </>
                )}
                {data.round.run_order === "reverse_previous" && data.round.stage > 1 && (
                  <button
                    disabled={
                      disabled ||
                      !props.rulesAllowed ||
                      !props.rulesEnabled ||
                      !props.manualEnabled ||
                      !props.roles.some((role) =>
                        ["admin", "producer", "head_judge", "chief_judge"].includes(role),
                      )
                    }
                    onClick={() =>
                      setLifecycle({
                        kind: "reorder-run2",
                        version: data.round.version,
                        guard: state.capture(),
                      })
                    }
                  >
                    Reorder run 2 from run 1
                  </button>
                )}
              </div>
            </details>
          )}
          {manual && data && (
            <form
              className="sc-round-form"
              onSubmit={(event) => {
                event.preventDefault();
                const source = data.round.riders?.find((r) => r.id === rider);
                if (source && target)
                  void state
                    .write(`${base}/heats/${source.heat_id}/riders/${source.id}/promote`, {
                      to_heat_id: target,
                    })
                    .then(() => setRider(""))
                    .catch(() => {});
              }}
            >
              <label>
                Rider
                <select
                  value={rider}
                  onChange={(e) => setRider(e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Choose rider</option>
                  {data.round.riders?.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Next heat
                <select
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Choose heat</option>
                  {data.targets
                    .filter(
                      (r) =>
                        r.stage > data.round.stage ||
                        (r.stage === data.round.stage && r.stage_order > data.round.stage_order),
                    )
                    .flatMap((r) =>
                      r.heats.map((h) => (
                        <option key={h.id} value={h.id}>
                          {r.name} · {h.name}
                        </option>
                      )),
                    )}
                </select>
              </label>
              <button type="submit" disabled={disabled || !rider || !target || !canManual}>
                Promote rider
              </button>
            </form>
          )}
          {state.message && <p role="status">{state.message}</p>}
          <ConfirmDialog
            open={!!lifecycle}
            title={
              lifecycle?.kind === "abandon"
                ? "Abandon this round?"
                : lifecycle?.kind === "resume"
                  ? "Resume this round?"
                  : "Reorder the second run?"
            }
            confirmLabel={
              lifecycle?.kind === "abandon"
                ? "Abandon round"
                : lifecycle?.kind === "resume"
                  ? "Resume round"
                  : "Reorder run 2"
            }
            onCancel={() => setLifecycle(null)}
            onConfirm={async () => {
              if (!lifecycle || !lifecycle.guard())
                throw new Error("The round changed. Refresh and review again.");
              if (lifecycle.kind === "abandon" && !reason.trim())
                throw new Error("Enter a reason for abandoning this round.");
              await state.write(
                `${path}/${lifecycle.kind}`,
                {
                  expected_version: lifecycle.version,
                  ...(lifecycle.kind === "abandon" ? { reason: reason.trim() } : {}),
                  ...(lifecycle.mode ? { mode: lifecycle.mode } : {}),
                },
                "post",
                lifecycle.guard,
              );
            }}
          >
            {lifecycle?.kind === "abandon" ? (
              <>
                <p>
                  Uncompleted heats will be abandoned and their timers stopped. Partial attempts are
                  kept for the crew and excluded from standings.
                </p>
                <label>
                  Reason
                  <textarea
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    maxLength={2000}
                  />
                </label>
              </>
            ) : lifecycle?.kind === "resume" ? (
              <p>
                {lifecycle.mode === "rerun"
                  ? "All heats restart. Every existing attempt is retained as superseded history. New attempt numbers continue after the largest recorded number."
                  : "Only uncompleted heats reopen. Completed heats and their results stay intact."}
              </p>
            ) : (
              <p>
                The lowest run-1 attempt total rides first. Ties keep run-1 running order; a missing
                value rides first. Open run-1 attempts must be ended first.
              </p>
            )}
          </ConfirmDialog>
          <ConfirmDialog
            open={!!confirm}
            title={confirm?.kind === "undo" ? "Undo progression?" : "Apply progression?"}
            confirmLabel={confirm?.kind === "undo" ? "Undo progression" : "Apply progression"}
            onCancel={() => setConfirm(null)}
            onConfirm={async () => {
              if (!confirm || !confirm.guard())
                throw new Error("The preview changed. Refresh and review again.");
              await state.write(
                `${path}/${confirm.kind === "undo" ? "undo" : "apply"}-progression`,
                {
                  expected_version: confirm.version,
                  ...(confirm.token ? { preview_token: confirm.token } : {}),
                },
                "post",
                confirm.guard,
              );
            }}
          >
            {confirm?.kind === "undo" ? (
              <p>
                {confirm.count} promoted riders will be removed from the destination heats. Original
                scores stay intact.
              </p>
            ) : (
              <p>
                {confirm?.count} {confirm?.count === 1 ? "rider will" : "riders will"} advance into
                the reviewed heats. Original scores stay intact.
              </p>
            )}
          </ConfirmDialog>
        </div>
      </RunSheet>
    </div>
  );
}
